import React, { useState, useEffect } from 'react';
import { Modal, Backdrop, Fade, Box, makeStyles } from '@material-ui/core';
import fontFamily from '../../../../assets/css/fonts';
import color from '../../../../assets/css/colors';
import Button from '../../../../components/Buttons/Button';
import Table, { columnType } from '../../../../components/ClearingTable/Table';
import {
  CustomBodyRenderCurrencyLeft,
  CustomBodyRenderQty,
  CustomBodyRenderPercentage,
} from '../../../../components/Table/CustomBodyRender';
import { listRequirementDetail } from '../../../../services/MarginRequirementService';
import { formatCurrency, formatQty, formatCurrencySymbol } from '../../../../lib/fmt';

const useStyles = makeStyles(() => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: 800,
    width: '100%',
    margin: '0 auto',
    float: 'clear',
  },
  modalHeader: {
    backgroundColor: 'white',
    padding: '30px 0px 0px 30px',
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    display: 'flex',
    '& > div:nth-child(1)': {
      flex: 'none',
      marginRight: 7,
      '& > svg': {
        width: 28,
        height: 28,
        '& path': {},
      },
    },
    '& div:nth-child(2)': {
      flex: 1,
    },
    '& div:nth-child(3)': {
      flex: 'none',
      '& .MuiIconButton-root': {
        borderRadius: 5,
        padding: 10,
        '&:hover svg path': {},
      },
      '& svg:last-child': {
        width: 15,
        height: 15,
      },
    },
  },
  modalBody: {
    backgroundColor: 'white',
    padding: '0px 10px',
    maxHeight: 1000,
    overflowY: 'auto',
    fontFamily: fontFamily.headers,
    color: color.default.main,
    '& p': {
      fontSize: 16,
      fontFamily: fontFamily.headers,
      fontWeight: 400,
      lineHeight: '26px',
      color: color.default.main,
      marginTop: 0,
    },
    '& hr': {
      margin: '30px 0px',
    },
  },
  modalFooter: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    backgroundColor: 'white',
    padding: '0px 25px 25px 25px',
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
    position: 'relative',
    '& button': {
      width: 120,
    },
  },
  incompleteSteps: {
    fontSize: 16,
    lineHeight: '24px',
    marginTop: 20,
    '& > label': {
      fontWeight: 600,
      marginBottom: 10,
      display: 'block',
    },
    '& ul': {
      listStyle: 'none',
      margin: '0px 0px 0px -40px',
      lineHeight: '30px',
      '& > li > span': {
        position: 'relative',
        top: 4,
        '& > svg': {
          width: 20,
          height: 20,
        },
      },
    },
  },
  success: {
    '& > svg > path': {},
  },
  error: {
    '& > svg > path': {},
  },
  closeButton: {
    '& button': {
      marginTop: '-10px !important',
      marginRight: '5px !important',
    },
  },
}));

export default function MarginRequirementDetails({
  onClose: handleClose,
  open: isOpen,
  value,
}) {
  const classes = useStyles();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [pagination, setPagination] = useState({});
  const [footerData, setFooterData] = useState({});
  const [totalRows, setTotalRows] = useState(0);

  useEffect(() => {
    const getRequirementDetails = async () => {
      //   try {
      //     setLoading(true);
      //     const data = await listRequirementDetail(value);
      //     setRows(data.requirementDetailsList);
      //   } catch (error) {
      //     console.error(error);
      //   }
      //   setLoading(false);
      handleSearch();
    };
    getRequirementDetails();
  }, [isOpen]);

  const columns = [
    {
      name: 'tradeDate',
      label: 'Trade Date',
      type: columnType.date,
    },
    {
      name: 'symbol',
      label: 'Symbol',
    },
    {
      name: 'cusip',
      label: 'Cusip',
    },
    {
      name: 'assetType',
      label: 'Asset Type',
    },
    {
      name: 'masterAccountNo',
      label: 'Master Account No',
    },
    {
      name: 'accountName',
      label: 'Account Name',
    },
    {
      name: 'leveragedFactor',
      label: 'Leveraged Factor',
      options: {
        customBodyRender: CustomBodyRenderQty,
      },
      type: 'quantity',
    },
    {
      name: 'closingPrice',
      label: 'Closing Price',
      type: columnType.price,
    },
    {
      name: 'longQty',
      label: 'Long Qty',
      options: {
        customBodyRender: CustomBodyRenderQty,
      },
      addFooter: true,
      type: columnType.quantity,
    },
    {
      name: 'shortQty',
      label: 'Short Qty',
      options: {
        customBodyRender: CustomBodyRenderQty,
      },
      addFooter: true,
      type: columnType.quantity,
    },
    {
      name: 'previousQty',
      label: 'Previous Qty',
      options: {
        customBodyRender: CustomBodyRenderQty,
      },
      addFooter: true,
      type: columnType.quantity,
    },
    {
      name: 'longMarketValue',
      label: 'Long Market Value',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'shortMarketValue',
      label: 'Short Market Value',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'marketValue',
      label: 'Market Value',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'previousMarketValue',
      label: 'Previous Market Value',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'exchangeReq',
      label: 'Exchange Req',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'exchangeRate',
      label: 'Exchange Rate',
      options: {
        customBodyRender: CustomBodyRenderPercentage,
      },
      type: columnType.percentage,
    },
    {
      name: 'houseReq',
      label: 'House Req',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'houseRate',
      label: 'House Rate',
      options: {
        customBodyRender: CustomBodyRenderPercentage,
      },
      type: columnType.percentage,
    },
    {
      name: 'fedReq',
      label: 'Fed Req',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'fedRate',
      label: 'Fed Rate',
      options: {
        customBodyRender: CustomBodyRenderPercentage,
      },
      type: columnType.percentage,
    },
    {
      name: 'pmHouseReq',
      label: 'PM House Req',
      type: columnType.amount,
      options: {
        display: false,
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
    },
    {
      name: 'pmHouseRate',
      label: 'PM House Rate',
      options: {
        display: false,
        customBodyRender: CustomBodyRenderPercentage,
      },
      type: columnType.percentage,
    },

    {
      name: 'costPrice',
      label: 'Cost Price',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.price,
    },
    {
      name: 'costBasis',
      label: 'Cost Basis',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'subAccountType',
      label: 'Sub Account Type',
      options: {
        display: false,
      },
    },
    {
      name: 'adjFedReq',
      label: 'Adjusted Fed Req',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'adjExchangeReq',
      label: 'Adjusted Exchange Req',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'adjHouseReq',
      label: 'Adjusted House Req',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'longOptionMarketValue',
      label: 'Long Option Market Value',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'shortOptionMarketValue',
      label: 'Short Option Market Value',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'timsReq',
      label: 'Tims Req',
      type: columnType.amount,
      addFooter: true,
      options: {
        display: false,
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
    },
    {
      name: 'timsRate',
      label: 'Tims Rate',
      options: {
        display: false,
        customBodyRender: CustomBodyRenderPercentage,
      },
      type: columnType.percentage,
    },
    {
      name: 'totalAdjustmentFedReq',
      label: 'Total Adjustment Fed Req',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'totalAdjustmentHouseReq',
      label: 'Total Adjustment House Req',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'totalAdjustmentExchangeReq',
      label: 'Total Adjustment Exchange Req',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'qty',
      label: 'Qty',
      options: {
        customBodyRender: CustomBodyRenderQty,
      },
      addFooter: true,
      type: 'quantity',
    },
    {
      name: 'side',
      label: 'Side',
    },
    {
      name: 'originalCusip',
      label: 'Original Cusip',
      options: {
        display: false,
      },
    },
    {
      name: 'marginable',
      label: 'Marginable',
      type: columnType.bool,
      options: {
        display: false,
      },
    },
    {
      name: 'a-selectAll',
      label: 'Select All',
      options: {
        display: true,
        print: false,
        download: false,
        filter: false,
        setCellHeaderProps: () => ({
          style: {
            width: '0px',
            display: 'table-cell',
            padding: 0,
            pointerEvents: 'none',
            fontSize: 0,
          },
        }),
      },
    },
  ];

  const options = {
    selectableRows: 'none',
    responsive: 'standard',
    serverSide: true,
  };

  const getCsvData = async () => {
    if (totalRows === 0) {
      return [];
    }
    let paginationCopy = {
      ...pagination,
      reload: false,
      pageNo: 0,
      rowsPerPage: totalRows,
    };

    const data = await listRequirementDetail(value, paginationCopy);
    return data.requirementDetailsList;
  };

  const handleSearch = async (paginationChange) => {
    try {
      setLoading(true);

      let paginationCopy = {
        ...pagination,
        reload: false,
        pageNo: paginationChange === true ? pagination.pageNo : 0,
        rowsPerPage: pagination.rowsPerPage || 100,
      };

      const data = await listRequirementDetail(value, paginationCopy);

      setTotalRows(data.summary.totalRows);

      paginationCopy.count = data.summary.totalRows;
      setPagination(paginationCopy);

      setRows(data.requirementDetailsList);

      setFooterData({
        longQty: formatQty(data.summary.longQty),
        shortQty: formatQty(data.summary.shortQty),
        previousQty: formatQty(data.summary.previousQty),
        longMarketValue: formatCurrency(data.summary.longMarketValue),
        shortMarketValue: formatCurrency(data.summary.shortMarketValue),
        marketValue: formatCurrency(data.summary.marketValue),
        previousMarketValue: formatCurrency(data.summary.previousMarketValue),
        exchangeReq: formatCurrency(data.summary.exchangeReq),
        houseReq: formatCurrency(data.summary.houseReq),
        pmHouseReq: formatCurrency(data.summary.pmHouseReq),
        timsReq: formatCurrency(data.summary.timsReq),
        fedReq: formatCurrency(data.summary.fedReq),
        costPrice: formatCurrencySymbol(data.summary.costPrice),
        costBasis: formatCurrency(data.summary.costBasis),
        adjFedReq: formatCurrency(data.summary.adjFedReq),
        adjExchangeReq: formatCurrency(data.summary.adjExchangeReq),
        adjHouseReq: formatCurrency(data.summary.adjHouseReq),
        longOptionMarketValue: formatCurrency(data.summary.longOptionMarketValue),
        shortOptionMarketValue: formatCurrency(data.summary.shortOptionMarketValue),
        totalAdjustmentFedReq: formatCurrency(data.summary.totalAdjustmentFedReq),
        totalAdjustmentHouseReq: formatCurrency(data.summary.totalAdjustmentHouseReq),
        totalAdjustmentExchangeReq: formatCurrency(
          data.summary.totalAdjustmentExchangeReq
        ),
        qty: formatQty(data.summary.qty),
      });
    } catch (error) {
      console.log(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      className={classes.modalBackdrop}
      open={isOpen}
      onClose={() => {
        handleClose();
      }}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Fade in={isOpen}>
        <div className={classes.tableModalFull}>
          <Box component="div" mt={10}>
            <form noValidate autoComplete="off">
              <div className={classes.modalHeader}></div>
              <div className={classes.modalBody}>
                <Table
                  title={'Margin Requirement Details'}
                  data={rows}
                  columns={columns}
                  options={options}
                  pagination={pagination}
                  setPagination={setPagination}
                  onSearch={handleSearch}
                  footerData={footerData}
                  getCsvData={getCsvData}
                />
              </div>
              <div className={classes.modalFooter}>
                <div
                  className={classes.grdCellNone}
                  style={{ marginRight: 10, marginTop: 10 }}
                >
                  <Button
                    color="primary"
                    label="Close"
                    onClick={() => {
                      handleClose();
                    }}
                  />
                </div>
              </div>
            </form>
          </Box>
        </div>
      </Fade>
    </Modal>
  );
}
